import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';

export type SchedulerEventStatus = 'active' | 'fired' | 'cancelled' | 'deleted' | 'failed';

export interface SchedulerEvent {
  id: string;
  schedule_name: string;
  event_type: string;
  source_id: string;
  tenant_id: string;
  fire_at: string;
  status: SchedulerEventStatus;
  group_name: string;
  fired_on?: string | null;
  deleted_on?: string | null;
  failure_reason?: string | null;
  payload?: Record<string, unknown> | null;
  created_on: string;
  updated_on: string;
}

export interface SchedulerEventsListResponse {
  success: boolean;
  message_code: number;
  message: string;
  data: SchedulerEvent[];
  total: number;
}

export interface SchedulerEventDetailResponse {
  success: boolean;
  message_code: number;
  message: string;
  data: SchedulerEvent;
}

export interface SchedulerSyncResponse {
  success: boolean;
  message_code: number;
  message: string;
  data: {
    eventbridge_active_count: number;
    local_active_count: number;
    orphaned_marked_deleted: string[];
  };
}

export interface ListSchedulerEventsParams {
  tenant_id?: string;
  event_type?: string;
  status?: string;
  from_date?: string;
  to_date?: string;
  limit?: number;
  skip?: number;
}

export const schedulerEventsApi = createApi({
  reducerPath: 'schedulerEventsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['SchedulerEvent'],
  endpoints: (builder) => ({
    listSchedulerEvents: builder.query<SchedulerEventsListResponse, ListSchedulerEventsParams | void>({
      query: (params) => ({
        url: '/api/v1/scheduler/events',
        params: params ?? undefined,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((e) => ({ type: 'SchedulerEvent' as const, id: e.schedule_name })),
              { type: 'SchedulerEvent' as const, id: 'LIST' },
            ]
          : [{ type: 'SchedulerEvent' as const, id: 'LIST' }],
    }),

    getSchedulerEvent: builder.query<SchedulerEventDetailResponse, string>({
      query: (scheduleName) => ({ url: `/api/v1/scheduler/events/${scheduleName}` }),
      providesTags: (_result, _error, scheduleName) => [{ type: 'SchedulerEvent', id: scheduleName }],
    }),

    cancelSchedulerEvent: builder.mutation<{ success: boolean; message_code: number; message: string; data: { schedule_name: string } }, string>({
      query: (scheduleName) => ({ url: `/api/v1/scheduler/events/${scheduleName}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, scheduleName) => [
        { type: 'SchedulerEvent', id: scheduleName },
        { type: 'SchedulerEvent', id: 'LIST' },
      ],
    }),

    syncSchedulerEvents: builder.mutation<SchedulerSyncResponse, void>({
      query: () => ({ url: '/api/v1/scheduler/events/sync', method: 'POST' }),
      invalidatesTags: [{ type: 'SchedulerEvent', id: 'LIST' }],
    }),
  }),
});

export const {
  useListSchedulerEventsQuery,
  useGetSchedulerEventQuery,
  useCancelSchedulerEventMutation,
  useSyncSchedulerEventsMutation,
} = schedulerEventsApi;
